import { Component, inject, Type } from '@angular/core';
import { LegacyOrder } from '@luggagehero/shared/interfaces';
import { SharedNotificationService } from '@luggagehero/shared/services/notification';
import { SharedOrderService } from '@luggagehero/shared/services/orders';
import { hasPricedCancellationFee, SharedUtilCancellation } from '@luggagehero/shared/util';

import { BookingActionBaseComponent } from './booking-action.base-component';
import { BookingActionInfo } from './booking-action-info';
import { ModifyBookingActionBaseComponent } from './modify-booking-action.base-component';

interface CancelReason {
  key: string;
  value: string;
  hidden: boolean;
}

@Component({
  template: '',
  standalone: false,
})
export abstract class CancelBookingActionBaseComponent extends BookingActionBaseComponent {
  private orderService = inject(SharedOrderService);
  private notify = inject(SharedNotificationService);

  public order: LegacyOrder;

  errorOccurred = false;
  errorMessage = '';
  cancelReason = '';
  cancelReasonCode = '';
  selectedReason = '';
  reasonsExpanded = false;

  cancelReasons: CancelReason[] = [
    {
      key: 'FOUND_OTHER_OPTION',
      value: this._translate('CANCEL_REASON_FOUND_OTHER_OPTION_SIMPLE'),
      hidden: false,
    },
    {
      key: 'UNSUITABLE_OPENING_HOURS',
      value: this._translate('CANCEL_REASON_UNSUITABLE_OPENING_HOURS'),
      hidden: false,
    },
    { key: 'CHANGE_OF_PLANS', value: this._translate('CANCEL_REASON_CHANGE_OF_PLANS'), hidden: true },
    { key: 'DUPLICATE_BOOKING', value: this._translate('CANCEL_REASON_DUPLICATE_BOOKING'), hidden: true },
    {
      key: 'UNABLE_TO_FIND_LOCATION',
      value: this._translate('CANCEL_REASON_UNABLE_TO_FIND_LOCATION'),
      hidden: true,
    },
    {
      key: 'DONT_UNDERSTAND_HOW_THIS_WORKS',
      value: this._translate('CANCEL_REASON_DONT_UNDERSTAND_HOW_THIS_WORKS'),
      hidden: true,
    },
  ];

  get hasFreeCancellation(): boolean {
    return this.order?.total === 0;
  }

  /**
   * The policy line above the reasons (AM-4788): free until the deadline, or that it has passed,
   * always with the instant at the storage location.
   *
   * With no quote — which for a legacy booking is forever, since `onBaseInit` returns early for
   * `schemaVersion < 3` and never fetches one — this renders the UNDATED policy line rather than
   * nothing, so the paragraph never loses the sentence master printed unconditionally. It is empty
   * only where there is a quote but nothing honest to say with it. The decision lives in
   * `SharedUtilCancellation` (pure, unit-tested); this only renders it.
   */
  get cancellationPolicyNote(): string {
    const note = SharedUtilCancellation.cancellationPolicyNote(
      this.order,
      this.booking,
      this.currentLang,
      // The cancel quote is a clone of the booking's order, so it carries the same pinned list —
      // the one that decides whether anything is actually charged past the deadline (round 8).
      hasPricedCancellationFee(this.order?.productList?.products),
    );
    return note ? this._translate(note.key, note.params) : '';
  }

  get isCancelAllowed(): boolean {
    return this.cancelReasonCode && !this.isLoading;
  }

  public get cancelationFee(): number {
    return this.order?.total || 0;
  }

  public get isLegacyBooking(): boolean {
    return this.booking?.schemaVersion < 3;
  }

  get modifyBookingActionInfo(): BookingActionInfo {
    return new BookingActionInfo('ModifyBookingFromCancelBooking')
      .withCallToAction(this._translate('MODIFY_BOOKING_CTA'))
      .withAction({ componentToShow: this.modifyBookingActionComponentType })
      .withTitle(this._translate('MODIFY_BOOKING_CTA'));
  }

  protected abstract get modifyBookingActionComponentType(): Type<ModifyBookingActionBaseComponent>;

  public async onBaseInit(): Promise<void> {
    if (this.isLegacyBooking) {
      return;
    }

    this.order = await this.orderService.requestCancellation(this.booking._id);
    this.cd.markForCheck();
  }

  async cancelBooking() {
    this.isLoading = true;

    try {
      const res = await (this.isLegacyBooking
        ? this.bookingService.cancelBooking(this.booking._id, this.cancelReason, this.cancelReasonCode)
        : this.bookingService.confirmCancellation(
            this.booking._id,
            this.order._id,
            this.cancelReason,
            this.cancelReasonCode,
          ));

      if (res.status !== 'CANCELLED') {
        const cancelError = this._translate('CANCEL_FAILED');
        this.notify.error(cancelError);
      }

      this.updateBooking(res);
      this.close();
    } catch (err) {
      const errorMessage = typeof err === 'string' ? err : (err as { message?: string })?.message || 'Unknown error';
      this.notify.error(errorMessage);
    }

    this.isLoading = false;
  }

  selectReason(selectedReasonCode: string) {
    this.cancelReasonCode = selectedReasonCode;
    this.selectedReason = this.cancelReasons.find((r) => r.key === selectedReasonCode)?.value;
    this.cd.markForCheck();
  }

  public modifyBooking() {
    // Close this action nicely (otherwise it will be destroyed by the new action without animation etc.)
    this.close();

    // Launch modify booking action instead
    this.actionRequest.emit(this.modifyBookingActionInfo);
  }
}
